/**
 * Desktop file sync, "Mirror" and "Stream" modes (docs/features/files-desktop-sync.md → IPC contract, plus
 * docs/features/files-desktop-stream.md → IPC changes). The Rust sync engine owns the
 * synced folder; these are its Tauri commands and the `sync://status` event, typed. Outside the desktop app the bridge
 * is unavailable (every call rejects, `onStatus` never fires), except in mock builds (VITE_MOCK), where an in-browser
 * fake (mock/desktopSync.ts) animates a sync so the screens can be built and screenshotted in a normal browser.
 */
import { isDesktop } from "./desktop.ts";

export type SyncState = "off" | "starting" | "idle" | "syncing" | "paused" | "offline" | "error";

/** Mirror: a full copy in a folder you pick. Stream: placeholders that download on open (File Provider / Cloud Files). */
export type SyncMode = "mirror" | "stream";

/** Why Stream isn't offered: macOS before 26, a build that can't register a File Provider domain, or Windows before it's verified. */
export type StreamReason = "unsupportedOs" | "unsigned" | "notVerified";

/** `sync_capabilities`. */
export interface SyncCapabilities {
  stream: boolean;
  streamReason: StreamReason | null;
  /** The fixed macOS CloudStorage folder for Stream; null when the person picks the folder. */
  streamRoot: string | null;
}

/** The i18n key of the reason Stream is off; an unknown or null reason reads as "not in this build". */
export function streamReasonKey(reason: StreamReason | null): "sync.mode.streamOff.unsupportedOs" | "sync.mode.streamOff.notVerified" | "sync.mode.streamOff.unsigned" {
  return reason === "unsupportedOs" ? "sync.mode.streamOff.unsupportedOs" : reason === "notVerified" ? "sync.mode.streamOff.notVerified" : "sync.mode.streamOff.unsigned";
}

/** An app without `sync_capabilities` (slice 2 builds) or a browser: Mirror only. */
export const MIRROR_ONLY: SyncCapabilities = { stream: false, streamReason: "unsigned", streamRoot: null };

export interface SyncTransfer {
  name: string;
  direction: "up" | "down";
  bytesDone: number;
  bytesTotal: number;
}

export interface SyncStatus {
  state: SyncState;
  workspaceId: string | null;
  workspaceSlug: string | null;
  root: string | null;
  mode: SyncMode;
  /** Ms epoch; null while paused = until resumed. */
  pausedUntil: number | null;
  lastSyncedAt: number | null;
  pendingUploads: number;
  pendingDownloads: number;
  bytesLeft: number;
  transfers: SyncTransfer[];
  conflicts: number;
  errors: { path: string; message: string }[];
  excludedFolderIds: string[];
}

export type SyncActivityKind = "uploaded" | "downloaded" | "newVersion" | "conflict" | "trashed" | "moved" | "created" | "error";

export interface SyncActivity {
  id: string;
  at: number;
  name: string;
  path: string;
  kind: SyncActivityKind;
  detail?: string;
}

export interface SyncConflict {
  id: string;
  at: number;
  /** The original (kept with the remote version), relative to the root. */
  path: string;
  /** The local bytes, saved as "<name> (conflicted copy from <device> <date>)". */
  conflictPath: string;
  itemId: string;
  localModifiedAt: number;
  remoteModifiedAt: number;
  remoteModifiedBy: string | null;
}

export type SyncConflictChoice = "keepBoth" | "useMine" | "useTheirs";

export interface SyncSettings {
  launchAtLogin: boolean;
  /** KB/s; null = no limit. */
  uploadLimitKbps: number | null;
  downloadLimitKbps: number | null;
}

export interface SyncSetupRequest {
  workspaceId: string;
  workspaceSlug: string;
  root: string;
  excludedFolderIds: string[];
  /** In Stream, `root` is `streamRoot` when the platform fixes it, and `excludedFolderIds` is empty (no selective sync). */
  mode: SyncMode;
}

export interface DesktopSync {
  /** False in a normal browser (not mock): the screens show nothing to sync with. */
  readonly available: boolean;
  status(): Promise<SyncStatus>;
  /** `sync_capabilities`; resolves `MIRROR_ONLY` on an app without the command. */
  capabilities(): Promise<SyncCapabilities>;
  setup(req: SyncSetupRequest): Promise<SyncStatus>;
  defaultRoot(): Promise<string>;
  /** The OS folder picker; null when cancelled. */
  pickFolder(): Promise<string | null>;
  /** `until` ms epoch, null = until resumed. */
  pause(until: number | null): Promise<SyncStatus>;
  resume(): Promise<SyncStatus>;
  setExcluded(excludedFolderIds: string[]): Promise<SyncStatus>;
  /** `sync_set_mode`: Mirror → Stream leaves the Mirror folder as plain files; Stream → Mirror uploads what's pending, then mirrors to the default folder. */
  setMode(mode: SyncMode): Promise<SyncStatus>;
  /** `sync_set_offline` (Stream only): keep a file or folder downloaded, or free it up. */
  setOffline(path: string, offline: boolean): Promise<void>;
  settings(): Promise<SyncSettings>;
  setSettings(s: SyncSettings): Promise<SyncSettings>;
  /** Newest first. */
  activity(): Promise<SyncActivity[]>;
  conflicts(): Promise<SyncConflict[]>;
  resolveConflict(id: string, choice: SyncConflictChoice): Promise<SyncConflict[]>;
  /** Reveals the synced folder (or a path inside it) in Finder / Explorer. */
  openFolder(path?: string): Promise<void>;
  /** The web saw `drive.changed`: poll the server now. */
  nudge(): Promise<void>;
  disconnect(): Promise<SyncStatus>;
  openPanel(): Promise<void>;
  openPreferences(): Promise<void>;
  /** Opens the setup window. Not in the spec's table: `sync_open_setup`, else a Bundle window on the route. */
  openSetup(workspaceSlug: string): Promise<void>;
  /** `sync://status`. */
  onStatus(cb: (s: SyncStatus) => void): Promise<() => void>;
  /** Closes this (setup / preferences / panel) window. */
  closeWindow(): Promise<void>;
  /** Brings the main Bundle window to the front. */
  showMainWindow(): Promise<void>;
  /** Quits the app (tauri-plugin-process `exit`). */
  quit(): Promise<void>;
}

/** The routes the sync windows load: `/{ws}/desktop/sync/{page}`. */
export type DesktopSyncPage = "setup" | "panel" | "preferences";

export function desktopSyncPageFromPath(path: string): DesktopSyncPage | undefined {
  const m = /^\/[^/]+\/desktop\/sync\/(setup|panel|preferences)\/?$/.exec(path.split(/[?#]/)[0]!);
  return m === null ? undefined : (m[1] as DesktopSyncPage);
}

export function desktopSyncPath(workspaceSlug: string, page: DesktopSyncPage): string {
  return `/${workspaceSlug}/desktop/sync/${page}`;
}

async function invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<T>(cmd, args);
}

const tauriSync: DesktopSync = {
  available: true,
  status: () => invoke("sync_status"),
  capabilities: () => invoke<SyncCapabilities>("sync_capabilities").catch(() => MIRROR_ONLY),
  setup: (req) => invoke("sync_setup", { ...req }),
  defaultRoot: () => invoke("sync_default_root"),
  pickFolder: () => invoke("sync_pick_folder"),
  pause: (until) => invoke("sync_pause", { until }),
  resume: () => invoke("sync_resume"),
  setExcluded: (excludedFolderIds) => invoke("sync_set_excluded", { excludedFolderIds }),
  setMode: (mode) => invoke("sync_set_mode", { mode }),
  setOffline: (path, offline) => invoke("sync_set_offline", { path, offline }),
  settings: () => invoke("sync_settings"),
  setSettings: (s) => invoke("sync_set_settings", { ...s }),
  activity: () => invoke("sync_activity"),
  conflicts: () => invoke("sync_conflicts"),
  resolveConflict: (id, choice) => invoke("sync_resolve_conflict", { id, choice }),
  openFolder: (path) => invoke("sync_open_folder", path === undefined ? {} : { path }),
  nudge: () => invoke("sync_nudge"),
  disconnect: () => invoke("sync_disconnect"),
  openPanel: () => invoke("sync_open_panel"),
  openPreferences: () => invoke("sync_open_preferences"),
  async openSetup(workspaceSlug) {
    try {
      await invoke("sync_open_setup");
    } catch {
      // An app without the command: a regular Bundle window on the setup route.
      await invoke<string>("open_window", { url: desktopSyncPath(workspaceSlug, "setup") });
    }
  },
  async onStatus(cb) {
    const { listen } = await import("@tauri-apps/api/event");
    return listen<SyncStatus>("sync://status", (e) => cb(e.payload));
  },
  async closeWindow() {
    const { getCurrentWebviewWindow } = await import("@tauri-apps/api/webviewWindow");
    await getCurrentWebviewWindow().close();
  },
  async showMainWindow() {
    const { Window } = await import("@tauri-apps/api/window");
    const main = await Window.getByLabel("main");
    if (main === null) return;
    await main.unminimize();
    await main.show();
    await main.setFocus();
  },
  quit: () => invoke("plugin:process|exit", { code: 0 }),
};

const unavailable = (): Promise<never> => Promise.reject(new Error("Desktop sync is only available in the Bundle desktop app"));

const noSync: DesktopSync = {
  available: false,
  status: unavailable,
  capabilities: () => Promise.resolve(MIRROR_ONLY),
  setup: unavailable,
  defaultRoot: unavailable,
  pickFolder: unavailable,
  pause: unavailable,
  resume: unavailable,
  setExcluded: unavailable,
  setMode: unavailable,
  setOffline: unavailable,
  settings: unavailable,
  setSettings: unavailable,
  activity: unavailable,
  conflicts: unavailable,
  resolveConflict: unavailable,
  openFolder: unavailable,
  nudge: () => Promise.resolve(),
  disconnect: unavailable,
  openPanel: unavailable,
  openPreferences: unavailable,
  openSetup: unavailable,
  onStatus: () => Promise.resolve(() => {}),
  closeWindow: () => Promise.resolve(window.close()),
  showMainWindow: () => Promise.resolve(),
  quit: () => Promise.resolve(),
};

/** An object whose methods wait for `target` (the mock loads lazily, only in mock builds). */
function deferred(target: Promise<DesktopSync>): DesktopSync {
  return new Proxy({ available: true } as DesktopSync, {
    get: (obj, key) =>
      key === "available" ? obj.available : key === "then" ? undefined : (...args: unknown[]) => target.then((t) => (t[key as keyof DesktopSync] as (...a: unknown[]) => unknown)(...args)),
  });
}

let mockSync: DesktopSync | undefined;

/** The desktop app's sync engine, the in-browser fake in mock builds, or an unavailable stub. */
export function desktopSync(mock: boolean): DesktopSync {
  if (isDesktop()) return tauriSync;
  if (!mock) return noSync;
  mockSync ??= deferred(import("./mock/desktopSync.ts").then((m) => m.createMockDesktopSync()));
  return mockSync;
}

/** Leading + trailing throttle for `sync_nudge` (a burst of `drive.changed` events → at most one call per `ms`). */
export function throttled(fn: () => void, ms: number, now: () => number = Date.now): () => void {
  let last = -Infinity;
  let timer: ReturnType<typeof setTimeout> | undefined;
  return () => {
    const wait = last + ms - now();
    if (wait <= 0) {
      last = now();
      fn();
    } else if (timer === undefined) {
      timer = setTimeout(() => {
        timer = undefined;
        last = now();
        fn();
      }, wait);
    }
  };
}

/** The next 8:00 tomorrow (local time), for "Pause until tomorrow". */
export function tomorrowAt8(now: number): number {
  const d = new Date(now);
  d.setDate(d.getDate() + 1);
  d.setHours(8, 0, 0, 0);
  return d.getTime();
}
